import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Text,
  Spacer,
  Select,
  Button,
  Modal,
  ModalBody,
  ModalClose,
  ModalFooter,
  Datepicker,
  Notify
} from '@flexisaf/flexibull2';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { ColorBox } from '../../styles/courses.style';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchApplications,
  searchApplicantsApplications,
} from 'redux/applicants/actions';
import {
  selectLoadingApplications,
  selectApplications,
} from 'redux/applicants/selector';
import { selectExportLoading } from 'redux/transaction-history/selectors';
import { exportTransactions } from 'redux/transaction-history/actions';
import Theme from 'utils/theme';

const ExportTransactionsModal = ({ transExport, setTransExport }) => {
  const dispatch = useDispatch();
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationOptions, setApplicationOptions] = useState([]);
  const [transactionStatus, setSelectedTransactionStatus] = useState(null);
  const [timeFrame, setTimeFrame] = useState([]);

  const applications = useSelector(selectApplications);
  const loadingApplication = useSelector(selectLoadingApplications);
  const exporting = useSelector(selectExportLoading);

  const sendQuery = (query) => dispatch(searchApplicantsApplications(query));
  const delayedQuery = debounce((q) => sendQuery(q), 500);
  const onSearchChange = (e) => {
    if (e) {
      delayedQuery(e);
    }
  };

  useEffect(() => {
    dispatch(fetchApplications());
  }, [dispatch]);

  useEffect(() => {
    const options = applications.map((a) => ({
      label: a.title,
      value: a.id,
      ...a,
    }));
    setApplicationOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications]);

  const exportReport = () => {
    const formData = {
      applicationId: selectedApplication?.id || null,
      statuses: transactionStatus
        ? [transactionStatus?.value.toUpperCase()]
        : ['PENDING', 'SUCCESS', 'FAILED'],
      startAt: timeFrame.length ? timeFrame[0].toISOString() : null,
      endAt: timeFrame.length ? timeFrame[1].toISOString() : null,
    };
    Object.keys(formData).forEach((key) => {
      if (formData[key] === null) {
        delete formData[key];
      }
    });
    dispatch(
      exportTransactions(formData, () => {
        reset();
        setTransExport(false);
        Notify('Your data is currently being generated and will be downloaded soon', {
          position: 'top-right',
          status: 'success',
        });
      })
    );
  };

  const reset = () => {
    setSelectedApplication(null);
    setSelectedTransactionStatus(null);
    setTimeFrame([]);
  };

  return (
    <Modal onClose={() => setTransExport(false)} open={transExport}>
      <ModalBody width="900px">
        <ModalClose onClick={() => setTransExport(false)}>&times;</ModalClose>
        <Box pad="40px">
          <Text size="20px" bold>
            Transactions Export
          </Text>
          <Spacer space="5px" />
          <Text block>Filter the transactions you want to export</Text>
          <Box margin="50px 0 20px">
            <Grid default="1fr 1fr">
              <Select
                width="100%"
                label="Select Applications"
                options={applicationOptions}
                value={selectedApplication}
                onChange={(e) => setSelectedApplication(e || null)}
                onInputChange={(e) => onSearchChange(e)}
                isLoading={loadingApplication}
                isClearable
              />
              <ColorBox>
                <Select
                  value={transactionStatus}
                  simpleOptions={['Pending', 'Success', 'Failed']}
                  elementOptions={[
                    {
                      label: 'Pending',
                      iconLeft: 'saf-circle tyellow',
                    },
                    {
                      label: 'Success',
                      iconLeft: 'saf-circle tgreen',
                    },
                    {
                      label: 'Failed',
                      iconLeft: 'saf-circle tred',
                    },
                  ]}
                  onChange={(transStatus) =>
                    setSelectedTransactionStatus(transStatus)
                  }
                  isClearable
                  block
                  label="Select status"
                />
              </ColorBox>
            </Grid>
            <Spacer space="40px" />
            <Grid default="1fr" lr="1fr" columnGap="30px">
              <Datepicker
                range
                label="Transaction Start Date"
                label2="End Date"
                rangeDivider="to"
                dayPlaceholder="DD"
                monthPlaceholder="MM"
                yearPlaceholder="YYYY"
                showDoubleView
                format="dd MMM yyyy"
                onChange={(date) => {
                  if (date === null || !isNaN(Date.parse(date[0]))) {
                    setTimeFrame(date || []);
                  }
                }}
                value={[timeFrame[0] || null, timeFrame[1] || null]}
              />
            </Grid>
            <Box margin="20px 0" pad="15px 0 0">
              <Text color={Theme.PrimaryRed}>
                N.B: The data file you are about exporting will be generated and
                downloaded automatically.
              </Text>
            </Box>
          </Box>
        </Box>

        <ModalFooter>
          <Box width="100%" align="right" pad="20px 40px">
            <Button
              onClick={() => exportReport()}
              progress={exporting}
              iconRight={<i className="flexibull-angle-right" />}
            >
              Generate & Download
            </Button>
          </Box>
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
};

export default ExportTransactionsModal;

ExportTransactionsModal.propTypes = {
  transExport: PropTypes.bool,
  setTransExport: PropTypes.func,
};
